<template>
  <v-dialog
    v-model="show"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    max-width="80vw"
    persistent
    scrollable
  >
    <v-card class="rounded-lg d-flex flex-column" style="max-height: 90vh">
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title
          class="text-h5 font-weight-bold"
          v-if="packageSelected"
        >
          <v-row>
            <v-col cols="auto" class="d-none d-md-flex">
              <v-icon large left>mdi-test-tube</v-icon>
            </v-col>
            <v-col cols="auto" class="d-none d-sm-flex">
              <span>
                {{
                  `Reacciones para ${packageSelected.name}` ||
                  "Configuración de Reacciones"
                }}
              </span>
            </v-col>
            <v-col cols="auto" class="d-flex d-sm-none">
              <span>Reacciones</span>
            </v-col>
          </v-row>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-container>
        <v-form ref="form" lazy-validation>
          <v-row>
            <v-col cols="12" md="6">
              <v-autocomplete
                ref="productAutocomplete"
                v-model="productSearch"
                :items="filteredProducts"
                label="Buscar productos (funcional)"
                placeholder="Escribe el nombre del producto para buscar"
                outlined
                dense
                hide-details
                item-text="name"
                item-value="id"
                :loading="productLoading"
                :search-input.sync="productSearchInput"
                :filter="filterProducts"
                @update:search-input="handleProductSearch"
                @click:clear="clearProductSearch"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="primary">mdi-magnify</v-icon>
                </template>
                <template v-slot:no-data>
                  <v-list-item
                    v-if="productSearchInput && productSearchInput.length > 2"
                  >
                    <v-list-item-icon>
                      <v-icon color="orange">mdi-plus-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        ¿Agregar "<strong>{{ productSearchInput }}</strong
                        >" como producto genérico?
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        Este producto no está en el catálogo oficial
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        small
                        color="orange"
                        dark
                        @click.stop="addCustomProduct"
                      >
                        Agregar
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-list-item v-else-if="productSearchInput">
                    <v-list-item-title>
                      Escribe al menos 3 caracteres para buscar
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-else>
                    <v-list-item-title>
                      Comienza a escribir para buscar productos
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }"> </template>
                <template v-slot:item="data">
                  <v-list-item @click="addProductToSelected(data.item)">
                    <v-list-item-content>
                      <v-list-item-title>
                        <span>{{ data.item.name }}</span>
                        <v-icon
                          v-if="data.item.isCustom"
                          x-small
                          color="orange"
                          class="ml-1"
                          >mdi-alert-circle</v-icon
                        >
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        {{ data.item.productId }}
                        <span v-if="data.item.isCustom" class="orange--text"
                          >(Producto genérico)</span
                        >
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="data.item.isCustom">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon color="orange" v-on="on"
                            >mdi-information</v-icon
                          >
                        </template>
                        <span
                          >Este producto no está en el catálogo oficial y se
                          considerará como genérico</span
                        >
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <div v-if="selectedProducts.length > 0" class="mt-2">
                <div class="caption grey--text mb-1">
                  Productos seleccionados:
                </div>
                <div class="d-flex flex-wrap">
                  <v-tooltip
                    top
                    v-for="(product, index) in selectedProducts"
                    :key="'product-' + index"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        v-bind="attrs"
                        v-on="on"
                        small
                        :color="product.isCustom ? 'orange' : 'primary'"
                        text-color="white"
                        class="mr-1 mb-1"
                        close
                        @click:close="removeProduct(index)"
                      >
                        <v-icon v-if="product.isCustom" x-small left
                          >mdi-alert</v-icon
                        >
                        {{ product.name }}
                      </v-chip>
                    </template>
                    <span>
                      <strong>{{ product.name }}</strong>
                      <div v-if="product.description">
                        {{ product.description }}
                      </div>
                      <div v-if="product.isCustom" class="mt-1 orange--text">
                        <v-icon x-small color="orange">mdi-information</v-icon>
                        Producto genérico (no está en el catálogo oficial)
                      </div>
                    </span>
                  </v-tooltip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <div class="flex-grow-0">
        <v-alert
          border="left"
          colored-border
          color="primary"
          elevation="0"
          class="ma-2 mb-0"
          dense
        >
          <v-icon small left color="primary">mdi-information</v-icon>
          <span class="caption">Las reacciones se numeran automáticamente</span>
        </v-alert>

        <v-card flat class="px-2 pt-1 pb-0">
          <v-row class="font-weight-bold" no-gutters>
            <v-col
              cols="1"
              md="4"
              class="text-center caption"
              v-if="!$vuetify.breakpoint.mobile"
              >#</v-col
            >
            <v-col cols="12" md="8" class="d-flex justify-space-around">
              <div class="text-center">
                <v-chip x-small color="fam" dark class="mr-1">FAM</v-chip>
                <span class="caption d-none d-sm-inline">(490-520nm)</span>
              </div>
              <div class="text-center">
                <v-chip x-small color="hex" dark class="mr-1">HEX</v-chip>
                <span class="caption d-none d-sm-inline">(535-555nm)</span>
              </div>
              <div class="text-center">
                <v-chip x-small color="cy5" dark class="mr-1">CY5</v-chip>
                <span class="caption d-none d-sm-inline">(650-670nm)</span>
              </div>
              <div class="text-center">
                <v-chip x-small color="green" dark class="mr-1"
                  >SY Green</v-chip
                >
                <span class="caption d-none d-sm-inline">(500-520nm)</span>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </div>

      <div class="flex-grow-1 overflow-y-auto px-2" ref="reactionsContainer">
        <draggable v-model="reactions" handle=".drag-handle" @end="onDragEnd">
          <transition-group name="list" tag="div">
            <v-card
              v-for="(reaction, index) in reactions"
              :key="reaction.id || index"
              outlined
              class="mb-2 rounded-lg"
            >
              <v-row class="align-center" no-gutters>
                <v-col cols="12" md="1" class="text-center pa-0">
                  <v-btn icon x-small class="drag-handle my-0 mx-1">
                    <v-icon x-small>mdi-drag-vertical</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="error lighten-2"
                    @click="removeReaction(index)"
                    :disabled="reactions.length <= 1"
                    class="my-0 mx-1"
                  >
                    <v-icon x-small>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-col>

                <v-col cols="12" md="2" class="text-center pa-0 pl-1">
                  <v-chip
                    x-small
                    color="primary"
                    dark
                    class="mx-0"
                    style="height: 28px"
                  >
                    <span class="mr-1">R{{ index + 1 }}-</span>
                    <v-text-field
                      v-model="reaction.customName"
                      dense
                      hide-details
                      single-line
                      class="name-input flex-grow-1"
                      style="max-width: 100px; min-width: 60px"
                      @input="updateReactionName(index)"
                    ></v-text-field>
                  </v-chip>
                </v-col>

                <!-- FAM -->
                <v-col cols="12" md="2" sm="3" class="px-1 py-1">
                  <v-combobox
                    v-model="reaction.fam"
                    :items="fluorophores"
                    placeholder="FAM"
                    dense
                    outlined
                    hide-details
                    clearable
                    :search-input.sync="famSearches[index]"
                    :loading="famLoading[index]"
                    item-text="name"
                    item-value="id"
                    :menu-props="{
                      maxHeight: '250px',
                      maxWidth: $vuetify.breakpoint.xsOnly
                        ? '250px'
                        : undefined,
                    }"
                    class="mt-0 mt-sm-1"
                    return-object
                    :disabled="!!reaction.syGreen"
                    @change="validateField(reaction.fam, index, 'fam')"
                    @update:search-input="
                      (val) => searchFluorophore(val, index, 'fam')
                    "
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.fam">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#4CAF50"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.fam.name }} -
                          {{ reaction.fam.molecularMarkerID }} - (
                          {{ getMoType(reaction.fam.moType) }}
                          <template v-if="reaction.fam.moType">
                            | {{ isPathogen(reaction.fam.isPathogen) }} )
                          </template>
                          <template v-else>
                            <span v-text="')'"></span>
                          </template>
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="famSearches[index]">
                            No hay resultados para "<strong>{{
                              famSearches[index]
                            }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.molecularMarkerID }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>
                  <!-- Alias input for FAM -->
                  <v-text-field
                    v-if="reaction.fam && reaction.fam.markerType !== 'MO'"
                    v-model="reaction.famAlias"
                    placeholder="Alias para FAM"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :rules="[
                      (v) => !v || v.length <= 20 || 'Máximo 20 caracteres',
                    ]"
                    prepend-inner-icon="mdi-tag"
                  >
                    <template v-slot:append v-if="reaction.famAlias">
                      <v-chip x-small color="primary" text-color="white">
                        {{ reaction.famAlias.length }}/20
                      </v-chip>
                    </template>
                  </v-text-field>

                  <!-- Functional Level for FAM -->
                  <v-combobox
                    v-if="reaction.fam && reaction.fam.markerType !== 'MO'"
                    v-model="reaction.famRiskLevel"
                    :items="riskLevels"
                    label="Nivel funcional"
                    placeholder="Buscar nivel funcional"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    item-text="name"
                    item-value="id"
                    return-object
                    :search-input.sync="famRiskSearches[index]"
                    :loading="famRiskLoading[index]"
                    @update:search-input="
                      (val) => searchRiskLevels(val, index, 'fam')
                    "
                    @change="(val) => validateFieldRiskLevel(val, index, 'fam')"
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.famRiskLevel">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#4CAF50"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.famRiskLevel.marker }} -
                          {{ reaction.famRiskLevel.description }}
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="famRiskSearches[index]">
                            No se encontraron niveles para "
                            <strong>{{ famRiskSearches[index] }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>{{
                          data.item.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip color="#4CAF50" x-small dark>
                          {{ data.item.marker }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-combobox>
                </v-col>

                <!-- HEX -->
                <v-col cols="12" md="2" sm="3" class="px-1 py-1">
                  <v-combobox
                    v-model="reaction.hex"
                    :items="fluorophores"
                    placeholder="HEX"
                    dense
                    outlined
                    hide-details
                    clearable
                    :search-input.sync="hexSearches[index]"
                    :loading="hexLoading[index]"
                    item-text="name"
                    item-value="id"
                    :menu-props="{
                      maxHeight: '250px',
                      maxWidth: $vuetify.breakpoint.xsOnly
                        ? '250px'
                        : undefined,
                    }"
                    class="mt-0 mt-sm-1"
                    return-object
                    :disabled="!!reaction.syGreen"
                    @change="validateField(reaction.hex, index, 'hex')"
                    @update:search-input="
                      (val) => searchFluorophore(val, index, 'hex')
                    "
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.hex">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#FFC107"
                            >mdi-circle</v-icon
                          >
                        </template>
                        {{ reaction.hex.name }} -
                        {{ reaction.hex.molecularMarkerID }} - (
                        {{ getMoType(reaction.hex.moType) }}
                        <template v-if="reaction.hex.moType">
                          | {{ isPathogen(reaction.hex.isPathogen) }} )
                        </template>
                        <template v-else>
                          <span v-text="')'"></span>
                        </template>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="hexSearches[index]">
                            No hay resultados para "<strong>{{
                              hexSearches[index]
                            }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.molecularMarkerID }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>

                  <!-- Alias input for HEX -->
                  <v-text-field
                    v-if="reaction.hex && reaction.hex.markerType !== 'MO'"
                    v-model="reaction.hexAlias"
                    placeholder="Alias para HEX"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :rules="[
                      (v) => !v || v.length <= 20 || 'Máximo 20 caracteres',
                    ]"
                    prepend-inner-icon="mdi-tag"
                  >
                    <template v-slot:append v-if="reaction.hexAlias">
                      <v-chip x-small color="primary" text-color="white">
                        {{ reaction.hexAlias.length }}/20
                      </v-chip>
                    </template>
                  </v-text-field>

                  <!-- Functional Level for HEX -->
                  <v-combobox
                    v-if="reaction.hex && reaction.hex.markerType !== 'MO'"
                    v-model="reaction.hexRiskLevel"
                    :items="riskLevels"
                    label="Nível funcional"
                    placeholder="Buscar nivel funcional"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    item-text="name"
                    item-value="id"
                    return-object
                    :search-input.sync="hexRiskSearches[index]"
                    :loading="hexRiskLoading[index]"
                    @update:search-input="
                      (val) => searchRiskLevels(val, index, 'hex')
                    "
                    @change="(val) => validateFieldRiskLevel(val, index, 'hex')"
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.hexRiskLevel">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#FFC107"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.hexRiskLevel.marker }} -
                          {{ reaction.hexRiskLevel.description }}
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="hexRiskSearches[index]">
                            No se encontraron niveles para "
                            <strong>{{ famRiskSearches[index] }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>{{
                          data.item.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip color="#4CAF50" x-small dark>
                          {{ data.item.marker }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-combobox>
                </v-col>

                <!-- CY5 -->
                <v-col cols="12" md="2" sm="3" class="px-1 py-1">
                  <v-combobox
                    v-model="reaction.cy5"
                    :items="fluorophores"
                    placeholder="CY5"
                    dense
                    outlined
                    hide-details
                    clearable
                    :search-input.sync="cy5Searches[index]"
                    :loading="cy5Loading[index]"
                    item-text="name"
                    item-value="id"
                    :menu-props="{
                      maxHeight: '250px',
                      maxWidth: $vuetify.breakpoint.xsOnly
                        ? '250px'
                        : undefined,
                    }"
                    class="mt-0 mt-sm-1"
                    return-object
                    :disabled="!!reaction.syGreen"
                    @change="validateField(reaction.cy5, index, 'cy5')"
                    @update:search-input="
                      (val) => searchFluorophore(val, index, 'cy5')
                    "
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.cy5">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#9C27B0"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.cy5.name }} -
                          {{ reaction.cy5.molecularMarkerID }} - (
                          {{ getMoType(reaction.cy5.moType) }}
                          <template v-if="reaction.cy5.moType">
                            | {{ isPathogen(reaction.cy5.isPathogen) }} )
                          </template>
                          <template v-else>
                            <span v-text="')'"></span>
                          </template>
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="cy5Searches[index]">
                            No hay resultados para "<strong>{{
                              cy5Searches[index]
                            }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.molecularMarkerID }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>

                  <!-- Alias input for CY5  -->
                  <v-text-field
                    v-if="reaction.cy5 && reaction.cy5.markerType !== 'MO'"
                    v-model="reaction.cy5Alias"
                    placeholder="Alias para CY5"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :rules="[
                      (v) => !v || v.length <= 20 || 'Máximo 20 caracteres',
                    ]"
                    prepend-inner-icon="mdi-tag"
                  >
                    <template v-slot:append v-if="reaction.cy5Alias">
                      <v-chip x-small color="primary" text-color="white">
                        {{ reaction.cy5Alias.length }}/20
                      </v-chip>
                    </template>
                  </v-text-field>

                  <!-- Functional Level for CY5-->
                  <v-combobox
                    v-if="reaction.cy5 && reaction.cy5.markerType !== 'MO'"
                    v-model="reaction.cy5RiskLevel"
                    :items="riskLevels"
                    label="Nível funcional"
                    placeholder="Buscar nivel funcional"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    item-text="name"
                    item-value="id"
                    return-object
                    :search-input.sync="cy5RiskSearches[index]"
                    :loading="cy5RiskLoading[index]"
                    @update:search-input="
                      (val) => searchRiskLevels(val, index, 'cy5')
                    "
                    @change="(val) => validateFieldRiskLevel(val, index, 'cy5')"
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.cy5RiskLevel">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="#9C27B0"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.cy5RiskLevel.marker }} -
                          {{ reaction.cy5RiskLevel.description }}
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="cy5RiskSearches[index]">
                            No se encontraron niveles para "
                            <strong>{{ famRiskSearches[index] }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>{{
                          data.item.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip color="#4CAF50" x-small dark>
                          {{ data.item.marker }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-combobox>
                </v-col>

                <!-- SYGREEN -->
                <v-col cols="12" md="2" sm="3" class="px-1 py-1">
                  <v-combobox
                    v-model="reaction.syGreen"
                    :items="fluorophores"
                    placeholder="SYGREEN"
                    dense
                    outlined
                    hide-details
                    clearable
                    :search-input.sync="syGreenSearches[index]"
                    :loading="syGreenLoading[index]"
                    item-text="name"
                    item-value="id"
                    :menu-props="{
                      maxHeight: '250px',
                      maxWidth: $vuetify.breakpoint.xsOnly
                        ? '250px'
                        : undefined,
                    }"
                    class="mt-0 mt-sm-1"
                    return-object
                    :disabled="
                      !!reaction.fam || !!reaction.hex || !!reaction.cy5
                    "
                    @change="validateField(reaction.syGreen, index, 'syGreen')"
                    @update:search-input="
                      (val) => searchFluorophore(val, index, 'syGreen')
                    "
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.syGreen">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="green"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.syGreen.name }} -
                          {{ reaction.syGreen.molecularMarkerID }} - (
                          {{ getMoType(reaction.syGreen.moType) }}
                          <template v-if="reaction.syGreen.moType">
                            | {{ isPathogen(reaction.syGreen.isPathogen) }} )
                          </template>
                          <template v-else>
                            <span v-text="')'"></span>
                          </template>
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="syGreenSearches[index]">
                            No hay resultados para "<strong>{{
                              syGreenSearches[index]
                            }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ data.item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.molecularMarkerID }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>

                  <!-- Alias input for SYGREEN -->
                  <v-text-field
                    v-if="
                      reaction.syGreen && reaction.syGreen.markerType !== 'MO'
                    "
                    v-model="reaction.syGreenAlias"
                    placeholder="Alias para SY Green"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    :rules="[
                      (v) => !v || v.length <= 20 || 'Máximo 20 caracteres',
                    ]"
                    prepend-inner-icon="mdi-tag"
                  >
                    <template v-slot:append v-if="reaction.syGreenAlias">
                      <v-chip x-small color="primary" text-color="white">
                        {{ reaction.syGreenAlias.length }}/20
                      </v-chip>
                    </template>
                  </v-text-field>

                  <!-- Functional Level for SYGREEN -->
                  <v-combobox
                    v-if="
                      reaction.syGreen && reaction.syGreen.markerType !== 'MO'
                    "
                    v-model="reaction.syGreenRiskLevel"
                    :items="riskLevels"
                    label="Nível funcional"
                    placeholder="Buscar nivel funcional"
                    dense
                    outlined
                    hide-details
                    class="mt-1"
                    item-text="name"
                    item-value="id"
                    return-object
                    :search-input.sync="syGreenRiskSearches[index]"
                    :loading="syGreenRiskLoading[index]"
                    @update:search-input="
                      (val) => searchRiskLevels(val, index, 'syGreen')
                    "
                    @change="
                      (val) => validateFieldRiskLevel(val, index, 'syGreen')
                    "
                  >
                    <template v-slot:prepend-inner>
                      <v-tooltip bottom v-if="reaction.syGreenRiskLevel">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" x-small color="green"
                            >mdi-circle</v-icon
                          >
                        </template>
                        <span>
                          {{ reaction.syGreenRiskLevel.marker }} -
                          {{ reaction.syGreenRiskLevel.description }}
                        </span>
                      </v-tooltip>
                      <v-icon v-else x-small color="gray">mdi-circle</v-icon>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-title>
                          <span v-if="syGreenRiskSearches[index]">
                            No se encontraron niveles para "<strong>{{
                              syGreenRiskSearches[index]
                            }}</strong
                            >"
                          </span>
                          <span v-else>Escribe para buscar</span>
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-slot:item="data">
                      <v-list-item-content>
                        <v-list-item-title>{{
                          data.item.name
                        }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">
                          {{ data.item.description }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip color="#4CAF50" x-small dark>
                          {{ data.item.marker }}
                        </v-chip>
                      </v-list-item-action>
                    </template>
                  </v-combobox>
                </v-col>
              </v-row>
            </v-card>
          </transition-group>
        </draggable>

        <v-btn
          v-if="!permissions.find((per) => per == 'lab_unedit')"
          color="primary"
          outlined
          class="mt-2 mb-2"
          @click="addReaction"
          block
          small
        >
          <v-icon left small>mdi-plus-circle-outline</v-icon>
          Agregar Reacción
        </v-btn>
      </div>

      <div class="flex-grow-0 px-2 pb-1">
        <v-textarea
          v-model="notes"
          label="Notas adicionales"
          placeholder="Ej: *Alternaria spp. V2*"
          outlined
          dense
          hide-details
          rows="2"
          class="rounded-lg caption"
        ></v-textarea>
      </div>

      <v-card-actions
        class="flex-grow-0 px-2 pb-2 pt-1"
        v-if="!permissions.find((per) => per == 'lab_unedit')"
      >
        <v-spacer></v-spacer>
        <v-btn
          color="grey darken-1"
          text
          @click="closeModal"
          :disabled="isLoading"
          class="px-2"
          small
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          dark
          depressed
          @click="saveReactions"
          :loading="isLoading"
          class="px-2 elevation-2"
          small
        >
          <v-icon left small>mdi-content-save-check</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import draggable from "vuedraggable";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  components: {
    draggable,
  },
  data: () => ({
    isLoading: false,
    reactions: [],
    notes: "",
    // Data related to reactions
    fluorophores: [],
    famSearches: [],
    hexSearches: [],
    cy5Searches: [],
    syGreenSearches: [],
    famLoading: [],
    hexLoading: [],
    cy5Loading: [],
    syGreenLoading: [],
    // Data for product search fields
    selectedProducts: [],
    productSearch: "",
    productSearchInput: "",
    productLoading: false,
    products: [],
    customProducts: [],
    // Data for risk levels per marker
    riskLevels: [],
    famRiskSearches: [],
    hexRiskSearches: [],
    cy5RiskSearches: [],
    syGreenRiskSearches: [],
    famRiskLoading: [],
    hexRiskLoading: [],
    cy5RiskLoading: [],
    syGreenRiskLoading: [],
  }),
  props: {
    // Controls modal visibility
    value: Boolean,
    // Laboratory information
    laboratory: {
      type: Object,
      default: () => ({ name: "-" }),
      required: true,
    },
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    // Vuex states
    ...mapState("session", ["permissions"]),
    ...mapState("laboratory", ["packageSelected"]),
    // Computed property to control modal open/close
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    // Products available for filtering
    filteredProducts() {
      return [...this.products];
    },
  },
  methods: {
    // Vuex actions and mutations
    ...mapActions("laboratory", ["getMarkers", "updatePackageReactions"]),
    ...mapMutations("laboratory", ["SET_PACKAGE_SELECTED"]),
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapActions("products", ["getProductByName"]),
    ...mapActions("riskLevel", ["getLevels"]),

    /**
     * Product search (mocked call)
     */
    async searchProducts(query) {
      this.productLoading = true;
      try {
        let params = {
          page: 0,
          search: query,
          limit: 20,
          select: { name: 1, productId: 1 },
        };
        let response = await this.getProductByName(params);
        this.products = response.products.items;
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "warning",
          text: `Error searching product: ${error.message}`,
          timeout: 2000,
        });
      } finally {
        this.productLoading = false;
      }
    },

    /**
     * Risk levels search (mocked call)
     */
    async searchRiskLevels(query, index, type) {
      if (!query) return;
      // Set loading for the specific type
      this[`${type}RiskLoading`][index] = true;
      try {
        let payload = {
          filter: {
            search: query,
            target: true,
          },
          per_page: 20,
          page: 1,
        };
        const response = await this.getLevels(payload);
        this.riskLevels = response.levels.map((element) => ({
          id: element._id,
          name: element.target,
          marker: element.molecularMarker.molecularMarkerID,
          description: `Levels: ${element.riskLevelMedium.min} - ${element.riskLevelMedium.max}`,
        }));
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "warning",
          text: `Error searching functional level: ${error.message}`,
          timeout: 2000,
        });
      } finally {
        this[`${type}RiskLoading`][index] = false;
      }
    },

    /**
     * Validate risk level field type
     */
    validateFieldRiskLevel(value, index, type) {
      if (typeof value !== "object") {
        this.$nextTick(() => {
          this.reactions[index][`${type}RiskLevel`] = null;
        });
      }
    },

    /**
     * Product filtering logic
     */
    filterProducts(item, queryText) {
      const text = item.name.toLowerCase();
      const searchText = queryText.toLowerCase();
      return text.indexOf(searchText) > -1;
    },

    /**
     * Product search handler
     */
    handleProductSearch(val) {
      if (val && val.length > 2) {
        this.searchProducts(val);
      }
    },

    /**
     * Clear product search inputs
     */
    clearProductSearch() {
      this.productSearchInput = "";
      this.productSearch = "";
      this.$nextTick(() => {
        if (this.$refs.productAutocomplete) {
          this.$refs.productAutocomplete.$el.querySelector("input").blur();
        }
      });
    },

    /**
     * Add custom/manual product
     */
    addCustomProduct() {
      if (this.productSearchInput && this.productSearchInput.length > 2) {
        const newProduct = {
          id: `custom-${Date.now()}`,
          name: this.productSearchInput,
          description: "Generic manually added product",
          isCustom: true,
        };

        this.customProducts.push(newProduct);
        this.selectedProducts.push(newProduct);
        this.clearProductSearch();

        this.SET_ALERT_TYPE({
          visible: true,
          type: "info",
          text: `"${newProduct.name}" was added as a generic product`,
          timeout: 3000,
        });
      }
    },

    /**
     * Add selected product from list
     */
    addProductToSelected(product) {
      const isAlreadySelected = this.selectedProducts.some(
        (p) => p.productId === product.productId
      );
      if (!isAlreadySelected) {
        this.selectedProducts.push(product);
        this.clearProductSearch();
      } else {
        this.SET_ALERT_TYPE({
          visible: true,
          type: "warning",
          text: `"${product.name}" is already selected`,
          timeout: 2000,
        });
      }
    },

    /**
     * Remove selected product
     */
    removeProduct(index) {
      const removedProduct = this.selectedProducts[index];
      this.selectedProducts.splice(index, 1);

      // If custom product, remove from custom list too
      if (removedProduct.isCustom) {
        const customIndex = this.customProducts.findIndex(
          (p) => p.id === removedProduct.id
        );
        if (customIndex !== -1) {
          this.customProducts.splice(customIndex, 1);
        }
      }
    },

    /**
     * Handles marker changes (validate type, reset values if MO type)
     */
    onMarkerChange(value, index, type) {
      if (typeof value !== "object") {
        this.$nextTick(() => {
          this.reactions[index][type] = null;
          this.reactions[index][`${type}Alias`] = "";
          this.reactions[index][`${type}RiskLevel`] = null;
        });
        return;
      }

      if (value && value.markerType === "MO") {
        this.reactions[index][`${type}Alias`] = "";
        this.reactions[index][`${type}RiskLevel`] = null;
      }
      this.$forceUpdate();
    },

    /**
     * General validation for fields and markers
     */
    validateField(value, index, type) {
      if (typeof value !== "object") {
        this.$nextTick(() => {
          this.reactions[index][type] = null;
          this.reactions[index][`${type}Alias`] = "";
          this.reactions[index][`${type}RiskLevel`] = null;
        });
        return;
      }

      // if (value && value.markerType === "MO") {
      //   this.$nextTick(() => {
      //     this.reactions[index]["syGreen"] = null;
      //     this.reactions[index][`syGreenAlias`] = "";
      //     this.reactions[index][`syGreenRiskLevel`] = null;
      //     this.reactions[index][`${type}Alias`] = "";
      //     this.reactions[index][`${type}RiskLevel`] = null;
      //   });
      // } else if (value && value.markerType !== "MO") {
      //   this.$nextTick(() => {
      //     // Reset all other markers if not MO
      //     this.reactions[index]["fam"] = null;
      //     this.reactions[index][`famAlias`] = "";
      //     this.reactions[index][`famRiskLevel`] = null;

      //     this.reactions[index]["hex"] = null;
      //     this.reactions[index][`hexAlias`] = "";
      //     this.reactions[index][`hexRiskLevel`] = null;

      //     this.reactions[index]["cy5"] = null;
      //     this.reactions[index][`cy5Alias`] = "";
      //     this.reactions[index][`cy5RiskLevel`] = null;
      //   });
      // }
      this.$forceUpdate();
    },

    /**
     * Initialize component data
     */
    initializeData() {
      this.notes = this.packageSelected.reactionObservations || "";
      const count = this.packageSelected.reactions?.length || 1;

      this.famSearches = Array(count).fill("");
      this.hexSearches = Array(count).fill("");
      this.cy5Searches = Array(count).fill("");
      this.syGreenSearches = Array(count).fill("");
      this.famLoading = Array(count).fill(false);
      this.hexLoading = Array(count).fill(false);
      this.cy5Loading = Array(count).fill(false);
      this.syGreenLoading = Array(count).fill(false);

      // Initialize risk level arrays
      this.famRiskSearches = Array(count).fill("");
      this.hexRiskSearches = Array(count).fill("");
      this.cy5RiskSearches = Array(count).fill("");
      this.syGreenRiskSearches = Array(count).fill("");
      this.famRiskLoading = Array(count).fill(false);
      this.hexRiskLoading = Array(count).fill(false);
      this.cy5RiskLoading = Array(count).fill(false);
      this.syGreenRiskLoading = Array(count).fill(false);

      this.reactions = this.packageSelected.reactions?.length
        ? this.packageSelected.reactions.map((r) => ({
            ...r,
            customName: r.name.split("-")[1] || "",
          }))
        : [this.createNewReaction()];

      this.selectedProducts.push(
        ...(this.packageSelected.functionalProducts || [])
      );
    },

    /**
     * Creates a new reaction object
     */
    createNewReaction() {
      return {
        name: `R${this.reactions.length + 1}-`,
        customName: "",
        fam: null,
        hex: null,
        cy5: null,
        syGreen: null,
        famAlias: "",
        hexAlias: "",
        cy5Alias: "",
        syGreenAlias: "",
        famRiskLevel: null,
        hexRiskLevel: null,
        cy5RiskLevel: null,
        syGreenRiskLevel: null,
      };
    },

    /**
     * Updates reaction name based on index and custom name
     */
    updateReactionName(index) {
      this.reactions[index].name = `R${index + 1}-${
        this.reactions[index].customName
      }`;
    },

    /**
     * Adds a new reaction and initializes its related arrays
     */
    addReaction() {
      const newReaction = this.createNewReaction();
      this.reactions.push(newReaction);
      this.famSearches.push("");
      this.hexSearches.push("");
      this.cy5Searches.push("");
      this.syGreenSearches.push("");
      this.famLoading.push(false);
      this.hexLoading.push(false);
      this.cy5Loading.push(false);
      this.syGreenLoading.push(false);

      this.famRiskSearches.push("");
      this.hexRiskSearches.push("");
      this.cy5RiskSearches.push("");
      this.syGreenRiskSearches.push("");
      this.famRiskLoading.push(false);
      this.hexRiskLoading.push(false);
      this.cy5RiskLoading.push(false);
      this.syGreenRiskLoading.push(false);

      this.$nextTick(() => {
        const container = this.$refs.reactionsContainer;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    },

    /**
     * Removes a reaction and its related state
     */
    removeReaction(index) {
      if (this.reactions.length > 1) {
        this.reactions.splice(index, 1);
        this.famSearches.splice(index, 1);
        this.hexSearches.splice(index, 1);
        this.cy5Searches.splice(index, 1);
        this.syGreenSearches.splice(index, 1);
        this.famLoading.splice(index, 1);
        this.hexLoading.splice(index, 1);
        this.cy5Loading.splice(index, 1);
        this.syGreenLoading.splice(index, 1);

        this.famRiskSearches.splice(index, 1);
        this.hexRiskSearches.splice(index, 1);
        this.cy5RiskSearches.splice(index, 1);
        this.syGreenRiskSearches.splice(index, 1);
        this.famRiskLoading.splice(index, 1);
        this.hexRiskLoading.splice(index, 1);
        this.cy5RiskLoading.splice(index, 1);
        this.syGreenRiskLoading.splice(index, 1);

        this.onDragEnd();
      }
    },

    /**
     * Searches fluorophores by type
     */
    async searchFluorophore(query, index, type) {
      this[`${type}Loading`][index] = true;
      try {
        const results = await this.fetchMarker(query);
        this.fluorophores = results;
      } catch (error) {
        console.error(`Error searching ${type}:`, error);
      } finally {
        this[`${type}Loading`][index] = false;
      }
    },

    /**
     * Fetch marker list from API
     */
    async fetchMarker(search = "") {
      let payload = {
        filter: {
          search,
        },
        per_page: 20,
        page: 1,
      };
      let response = await this.getMarkers(payload);
      return response.molecularMarkers.map((marker) => {
        const baseName =
          marker.markerType == "MO"
            ? marker.microorganism_meta.name
            : marker.soilFunction.es;

        const uniqueName = `${baseName} (${marker.molecularMarkerID})`;

        return {
          _id: marker._id,
          name: uniqueName,
          moType: marker.microorganism_meta
            ? marker.microorganism_meta.moType
            : undefined,
          markerId: marker._id,
          isPathogen: marker.isPathogen || false,
          molecularMarkerID: marker.molecularMarkerID,
          markerType: marker.markerType,
          metabolicPathway:
            marker.markerType != "MO" ? marker.process.es : null,
        };
      });
    },

    /**
     * Save reactions and products to API
     */
    async saveReactions() {
      try {
        if (!this.$refs.form.validate()) return;

        const customProducts = this.selectedProducts.filter((p) => p.isCustom);
        if (customProducts.length > 0) {
          const productNames = customProducts
            .map((p) => `"${p.name}"`)
            .join(", ");
          this.SET_ALERT_TYPE({
            visible: true,
            type: "warning",
            text: `The products ${productNames} are generic and not part of the official catalog`,
            timeout: 6000,
          });
        }

        this.isLoading = true;
        this.reactions = this.reactions.map((reaction) => {
          return {
            ...reaction,
            famPathogen: reaction.fam ? reaction.fam.isPathogen : false,
            hexPathogen: reaction.hex ? reaction.hex.isPathogen : false,
            cy5Pathogen: reaction.cy5 ? reaction.cy5.isPathogen : false,
            syGreenPathogen: reaction.syGreen
              ? reaction.syGreen.isPathogen
              : false,
          };
        });

        let payload = {
          reactions: this.reactions,
          reactionObservations: this.notes,
          functionalProducts: this.selectedProducts,
        };
        let params = {
          packageId: this.packageSelected._id,
        };

        let response = await this.updatePackageReactions({
          body: payload,
          params,
        });

        this.SET_PACKAGE_SELECTED(response.packageUpdated);
        this.$emit("packages_updated");
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Reactions saved successfully`,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Something went wrong. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Close modal
     */
    closeModal() {
      this.show = false;
    },

    /**
     * Returns microorganism type name
     */
    getMoType(moType) {
      if (moType == "nematode") {
        return "Nematode";
      } else if (moType == "bacteria") {
        return "Bacteria";
      } else if (moType == "fungi") {
        return "Fungus";
      } else {
        return "Functional";
      }
    },

    /**
     * Returns pathogen status
     */
    isPathogen(value) {
      return value ? "Pathogen" : "Beneficial";
    },

    /**
     * Updates reaction names when drag-and-drop order changes
     */
    onDragEnd() {
      this.reactions.forEach((reaction, index) => {
        reaction.name = `R${index + 1}-${reaction.customName}`;
      });
    },
  },
  beforeDestroy() {
    this.SET_PACKAGE_SELECTED(null);
  },
  watch: {
    /**
     * Watches product search input to trigger product search
     */
    productSearchInput(val) {
      if (val && val !== this.selectedProducts) {
        this.searchProducts(val);
      }
    },
    /**
     * Watches reactions deeply to trigger UI re-render
     */
    reactions: {
      deep: true,
      handler() {
        this.$forceUpdate();
      },
    },
  },
};
</script>

<style scoped>
.v-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.flex-grow-1 {
  flex: 1 1 auto;
  min-height: 0;
}

.overflow-y-auto {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.flex-grow-0 {
  flex: 0 0 auto;
}

.fam {
  background-color: #4caf50 !important;
}

.hex {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.cy5 {
  background-color: #9c27b0 !important;
}

.v-card:hover {
  box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.1), 0 5px 8px 0 rgba(0, 0, 0, 0.07),
    0 1px 14px 0 rgba(0, 0, 0, 0.06) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-chip {
  min-width: 40px;
  justify-content: center;
  font-weight: 600;
  font-size: 0.8rem;
  height: 24px;
}

.v-btn {
  letter-spacing: normal;
}

.v-list-item--highlighted {
  font-style: italic;
}

.drag-handle {
  cursor: move;
  cursor: -webkit-grab;
  cursor: grab;
}

.drag-handle:active {
  cursor: -webkit-grabbing;
  cursor: grabbing;
}

.sortable-chosen {
  opacity: 0.8;
  background: #f5f5f5;
}

.sortable-ghost {
  opacity: 0.4;
  background: #e0e0e0;
}

.name-input >>> .v-input__slot {
  margin: 0 !important;
  padding: 0 !important;
  min-height: auto !important;
}

.name-input >>> .v-text-field__details {
  display: none;
}

.name-input >>> input {
  font-size: 0.8rem;
  padding: 0 4px !important;
  height: 20px;
}

@media (max-width: 600px) {
  .v-dialog {
    margin: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
  }

  .v-card {
    max-height: 100vh;
    border-radius: 0;
  }

  .v-toolbar__title {
    font-size: 1.1rem !important;
  }

  .v-combobox {
    font-size: 0.8rem;
  }

  .v-textarea {
    font-size: 0.8rem;
  }

  .v-chip {
    padding: 0 4px;
  }

  .name-input >>> input {
    font-size: 0.5rem;
    padding: 0 2px !important;
    width: 60px !important;
  }
}
</style>
